import React, { useEffect, useState } from 'react';
import { Box, SpaceBetween, Modal, FormField, Button, Input, Alert, Select } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import StringUtils from 'src/utils/stringUtils';
import {JEWorksheetTemplate} from "src/models/je-allocation/JEWorksheetList";
import CreateJEWorksheetRequest from "src/models/je-allocation/CreateJEWorksheetRequest";
import JEPlaceholder from "src/models/je-allocation/JEPlaceholder";
import JEPlaceholderAutosuggest from "src/components/jeAllocation/shared/JEPlaceholderAutosuggest";

export default function CreateJEWorksheetModal(props: {visible: boolean,
    templates: JEWorksheetTemplate[],
    sevenSegmentLOVMap: Map<string, string[]>,
    isCreating: boolean,
    createError: string,
    onClose: () => void,
    onSubmit: (c: CreateJEWorksheetRequest) => any }) {

    const { visible, templates, sevenSegmentLOVMap, isCreating, createError, onClose, onSubmit } = props;

    const [jeWorksheetName, setJEWorksheetName] = useState('');
    const [selectedJETemplate, setSelectedJETemplate] = useState(null as unknown as any);
    const [activeJETemplates, setActiveJETemplates] = useState([] as any[]);
    const [jePlaceholderMap, setJEPlaceholderMap] = useState(null as unknown as Map<string, JEPlaceholder[]>);
    const [jePlaceholderValueMap, setJEPlaceholderValueMap] = useState(null as unknown as Map<string, string>);
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        setActiveJETemplates([{ label: CONSTANTS.CLEAR_SELECTION, value: CONSTANTS.CLEAR_SELECTION },
            ...templates.filter(x => x.templateVersion == CONSTANTS.CALCULATION_VERSIONS.CURRENT_VERSION).map(x => ({ label: x.templateName, value: x.templateId }))]);
    }, [templates])


    useEffect(() => {
        if (visible) {
            setJEWorksheetName('');
            setSelectedJETemplate(null as unknown as any);
            setShowError(false);
        }
    }, [visible])

    useEffect(() => {
        if (isCreating) {
            setShowError(true);
        }
    }, [isCreating])

    useEffect(() => {
        setJEPlaceholderMap(null as unknown as Map<string, JEPlaceholder[]>);
        setJEPlaceholderValueMap(null as unknown as Map<string, string>);
        if (selectedJETemplate != null) {
            const templateObject = templates.find(x => x.templateId == (selectedJETemplate as any)['value']);
            if (templateObject != null) {
                setJEPlaceholderMap(templateObject.placeholdersMap);
                const placeholderValues = new Map<string, string>();
                templateObject.placeholdersMap.forEach((v, k) => {
                    v?.forEach(x => placeholderValues.set(x.placeholderId, x.value || ''));
                });
                setJEPlaceholderValueMap(placeholderValues);
            }
        }
    }, [selectedJETemplate])

    const disableCreate = () => {
        if (StringUtils.isNullOrEmpty(jeWorksheetName)) {
            return true;
        }
        if (selectedJETemplate != null && jePlaceholderValueMap != null) {
            return Array.from(jePlaceholderValueMap.values()).filter(x => StringUtils.isNullOrEmpty(x)).length > 0
        }
        return false;
    }

    const selectJETemplate = (selectedOption: any) => {
        if (selectedOption.value == CONSTANTS.CLEAR_SELECTION) {
            setSelectedJETemplate(null as unknown as any);
        } else {
            setSelectedJETemplate(selectedOption);
        }
    }

    const setJEPlaceholderValue = (ph: JEPlaceholder, value: string) => {
        const jePlaceholderValueMapCopy = new Map(jePlaceholderValueMap);
        jePlaceholderValueMapCopy.set(ph.placeholderId, value);
        setJEPlaceholderValueMap(jePlaceholderValueMapCopy);
    }

    const submit = () => {
        const createWorksheetRequest: CreateJEWorksheetRequest = {
            worksheetName: jeWorksheetName,
            templateId: selectedJETemplate != null ? selectedJETemplate.value : undefined,
            placeholders: jePlaceholderValueMap != null ? [... Array.from(jePlaceholderValueMap.keys()).map(x => {
                return { placeholderId: x, value: jePlaceholderValueMap.get(x) } as JEPlaceholder
            })] : undefined
        }
        onSubmit(createWorksheetRequest);
    }

    return <Modal data-class="createJEWorksheetModal" visible={visible}
                  header="Create new JE Allocation worksheet"
                  size={selectedJETemplate == null ? "medium" : "large"}
                  onDismiss={onClose}
                  footer={
                      <Box float="right">
                          <Button variant="link" className="cancelButton" disabled={isCreating} onClick={onClose}>Cancel</Button>
                          <Button variant="primary" className="saveButton" disabled={isCreating || disableCreate()} onClick={submit}>{isCreating ? 'Creating...' : 'Create JE worksheet'}</Button>
                      </Box>
                  }>
        <SpaceBetween direction="vertical" size="m">
            <FormField  data-class="createJEModalField"
                        label="Enter JE worksheet name*"
                        stretch={false}
            >
                <Input data-class="basicModalInput jeWorksheetNameFieldInput"
                       value={jeWorksheetName}
                       onChange={e => setJEWorksheetName(e.detail.value)}
                />
            </FormField>
            <FormField  data-class="createModalField"
                        label="Select JE template"
                        stretch={false}
            >
                <Select data-class="basicModalDropdown jetTemplateFieldDropdown"
                        selectedOption={selectedJETemplate}
                        options={activeJETemplates}
                        onChange={({ detail }) => selectJETemplate(detail.selectedOption)}
                        placeholder="No template selected"
                />
            </FormField>
            {jePlaceholderMap != null &&
                <div className="jePlaceholderContainer">
                    <div className="jePlaceholderMainHeader">Enter placeholder values*</div>
                    {Array.from(jePlaceholderMap.keys()).map(k =>
                        <React.Fragment>
                            <div key={`jePlaceholderLabel${k}`}>{k}</div>
                            <div key={`jePlaceholderValue${k}`} className="jePlaceholderList">
                                {jePlaceholderMap.get(k)?.map(ph =>
                                    <JEPlaceholderAutosuggest jePlaceholder={ph} lov={sevenSegmentLOVMap.get(k) || []} onBlur={setJEPlaceholderValue} />
                                )}
                            </div>
                        </React.Fragment>
                    )}
                </div>
            }
            { showError && !StringUtils.isNullOrEmpty(createError) && <Alert type='error' dismissible={false}>{createError}</Alert> }
        </SpaceBetween>
    </Modal>
}
